import React, { FC, useEffect, useState } from 'react';
import { getMiniPlayerProps, buildEpisodeTitle } from 'utils/fnData';
import { getErrorStatus } from 'utils/fnError';
import { useParams } from 'react-router';
import { useDataFetcher } from 'hooks/useDataFetcher/useDataFetcher';
import { PlayerEvents, PlayingAssetType, VodPlayerAssetInfo } from 'types/Player';
import GSApi from 'api/Api';
import { usePlayer } from 'providers/player/PlayerContext';
import { useNavigation } from 'hooks/useNavigation';
import { useApp } from 'providers/useApp/AppContext';
import { getSeriesDetailsUrl } from 'utils/fnUrl';
import { PageErrorNotFound } from 'components/Pages/PageErrorNotFound/PageErrorNotFound';
import { PlayerWrapper } from 'components/Player/PlayerWrapper';
import { useAuth } from 'providers/useAuth/AuthContext';
import { FetchParamById, StreamIDKey } from 'types/ApiTypes';
import { useGlobalNetworkError, useGlobalNoInternet, withNetworkCheck } from 'hooks/withNetworkCheck/withNetworkCheck';
import { HelmetWrapper } from 'components/HelmetWrapper/HelmetWrapper';
import { getPlayerPageHelmetData } from 'utils/fnHelmet';
import { withPlayer } from 'hooks/withPlayer/withPlayer';
import { movieDetailsParser } from 'utils/fnParser';
import { usePersistentState } from 'hooks/useStorage/useStorage';
import CastHandler from 'providers/cast/CastHandler';
import PlaybackManager from 'providers/player/PlaybackManager';
import PageTakeover from 'components/Pages/Settings/PageTakeover/PageTakeover';

const VodPlayerPage: FC<{ assetId: string }> = ({ assetId }) => {
    const { onNoInternet } = useGlobalNoInternet();
    const { onNetworkError } = useGlobalNetworkError();
    const { assetId: assetIdFromUrl } = useParams<{ assetId: string }>();
    const [currentAssetId, setCurrentAssetId] = useState(assetIdFromUrl || assetId);
    const { store: playbackPositions } = usePersistentState('playbackPositions', {});

    const {
        response,
        error,
        loading,
        fetcher: createStreamSession,
        reset,
    } = useDataFetcher<any, FetchParamById>(
        (params) => {
            setCurrentAssetId(assetIdFromUrl || assetId);
            return GSApi.createStreamSession('VOD', StreamIDKey.VOD_ASSET_ID, params.id || assetIdFromUrl, {}, (res) => res);
        },
        onNoInternet,
        onNetworkError
    );

    const { response: movieDetails, fetcher: movieDetailsFetcher } = useDataFetcher<any, FetchParamById>(
        (param) => GSApi.fetchVodMovieDetails(param.id, movieDetailsParser),
        onNoInternet,
        onNetworkError
    );

    const { isGuest, openGuestAlert, isSignedOutNotice } = useAuth();
    const { appLanguage } = useApp();
    const { onBackPressed, navigate } = useNavigation();
    const {
        setAsset,
        addEventListener,
        removeEventListener,
        resetPlayerAsset,
        isMini,
        closeMiniPlayer,
        nextEpisode,
        nextEpisodeWatchable,
        updateMiniPlayerProps,
        setDisplayBingeWatch,
    } = usePlayer();

    const vodSeriesId = movieDetails?.vod_series_id;
    const nextEpisodeBlocked = !!nextEpisode && !nextEpisodeWatchable;

    const onStreamEndReached = () => {
        setDisplayBingeWatch(false);
        if (isMini) {
            if (nextEpisode && nextEpisodeWatchable) {
                updateMiniPlayerProps(getMiniPlayerProps(nextEpisode, PlayingAssetType.VOD));
            }
            closeMiniPlayer();
        } else if (nextEpisodeBlocked && vodSeriesId) {
            navigate(getSeriesDetailsUrl(vodSeriesId, true, null, appLanguage), { replace: true });
        } else {
            onBackPressed();
        }
    };

    useEffect(() => {
        const nextAssetId = assetIdFromUrl || assetId;

        if (nextAssetId && nextAssetId !== currentAssetId) {
            setCurrentAssetId(nextAssetId);
        }
    }, [assetIdFromUrl, assetId]);

    useEffect(() => {
        if (currentAssetId) {
            movieDetailsFetcher({ id: currentAssetId });
            if (!CastHandler.isCast()) {
                createStreamSession({ id: currentAssetId });
            }
        }
    }, [currentAssetId]);

    useEffect(() => {
        if (response && movieDetails && playbackPositions) {
            const assetToSet: VodPlayerAssetInfo = {
                ...response,
                type: 'vod', // movieDetails.type,
                id: movieDetails.id,
                title: movieDetails.title,
                titleId: response.vod_asset_id,
                image: movieDetails.background_image_url || movieDetails.image,
                releaseYear: movieDetails.release_year,
                countries: movieDetails.production_countries,
                isMovie: movieDetails.kind === 'Movie',
                duration: movieDetails.duration,
                genres: movieDetails.genres,
                cast: movieDetails.cast,
                captionLanguages: movieDetails.subtitle_languages,
                originalLanguages: movieDetails.audio_languages,
                description: movieDetails.description,
                startTime: movieDetails.vod_terms[0].start,
                endTime: movieDetails.vod_terms[0].end,
                heartbeat_interval: response.heartbeat_interval,
                bookmark: playbackPositions?.vod_asset_playback_positions?.[movieDetails.id],
                vodSeriesId: movieDetails.vod_series_id,
                seasonNumber: movieDetails.season_number,
                episodeNumber: movieDetails.episode_number,
                subtitle: buildEpisodeTitle(movieDetails.episodeTitle, movieDetails.season_number, movieDetails.episode_number),
            };

            PlaybackManager.adoptSession(response.id, response.heartbeat_interval);

            setAsset(assetToSet);
        }
    }, [response, movieDetails, playbackPositions]);

    useEffect(() => {
        addEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);

        return () => {
            removeEventListener(PlayerEvents.END_OF_STREAM, onStreamEndReached);
        };
    }, [response, isMini, nextEpisode, nextEpisodeWatchable, vodSeriesId, appLanguage]);

    useEffect(() => {
        resetPlayerAsset();

        if (CastHandler.isCast()) {
            const castAsset = PlaybackManager.getPlayingAsset();
            if (castAsset) {
                setAsset(castAsset);
            }
        }
    }, [currentAssetId]);

    useEffect(() => {
        if (isGuest) {
            openGuestAlert('/');
        }
    }, []);

    if (error && !isSignedOutNotice) {
        if (getErrorStatus(error) === 409) {
            return (
                <PageTakeover
                    limitReached
                    onSessionDeleted={() => {
                        reset();
                        createStreamSession({ id: currentAssetId });
                    }}
                />
            );
        }

        return <PageErrorNotFound onButtonClick={closeMiniPlayer} />;
    }

    return (
        <>
            {movieDetails && <HelmetWrapper {...getPlayerPageHelmetData(movieDetails)} />}
            <PlayerWrapper loading={loading} />
        </>
    );
};

export default withNetworkCheck(withPlayer(VodPlayerPage));
